import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiHeart } from 'react-icons/fi';
import useWishlistStore from '../store/useWishlistStore';
import useAuthStore from '../store/useAuthStore';

const BADGE_COLORS = {
  Bestseller: { bg: 'var(--primary)', color: '#FFF' },
  New: { bg: 'var(--gold)', color: '#FFF' },
  Festive: { bg: 'var(--primary-soft)', color: 'var(--primary)' },
  Limited: { bg: 'var(--ink)', color: '#FFF' },
  Editor: { bg: 'var(--gold-soft)', color: 'var(--gold)' },
  Sale: { bg: 'var(--ok)', color: '#FFF' },
};

export default function ProductCard({ product }) {
  const { toggle, isInWishlist } = useWishlistStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const inWish = isInWishlist(product._id);
  const discount = Math.round(((product.mrp - product.price) / product.mrp) * 100);
  const badgeStyle = product.badge ? BADGE_COLORS[product.badge] : null;

  const handleWishlist = (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    toggle(product._id);
  };

  return (
    <Link to={`/product/${product.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <motion.div
        whileHover="hover"
        initial="rest"
        animate="rest"
        style={{
          background: 'rgba(255,255,255,0.6)',
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid rgba(229,219,200,0.6)',
          backdropFilter: 'blur(20px) saturate(160%)',
          WebkitBackdropFilter: 'blur(20px) saturate(160%)',
          position: 'relative',
          transition: 'box-shadow 0.3s ease',
        }}
        variants={{
          rest: { boxShadow: '0 4px 12px rgba(42,31,26,0.04)' },
          hover: { boxShadow: '0 24px 48px rgba(42,31,26,0.12)' },
        }}
      >
        {/* Image */}
        <div style={{ position: 'relative', aspectRatio: '3/4', background: 'var(--surface-2)', overflow: 'hidden' }}>
          {product.images?.[0]?.url ? (
            <motion.img
              src={product.images[0].url}
              alt={product.name}
              loading="lazy"
              variants={{ rest: { scale: 1 }, hover: { scale: 1.06 } }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <KurtiPlaceholder color={product.colors?.[0]?.hex || '#7A1F2B'} />
          )}

          {/* Hover overlay sheen */}
          <motion.div
            variants={{ rest: { opacity: 0 }, hover: { opacity: 1 } }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'radial-gradient(circle at 50% 100%, rgba(176,138,62,0.18), transparent 65%)',
            }}
          />

          {/* Badges */}
          {badgeStyle && (
            <motion.span
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              style={{
                position: 'absolute', top: 12, left: 12,
                fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
                padding: '5px 10px', borderRadius: 999,
                background: badgeStyle.bg, color: badgeStyle.color,
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {product.badge}
            </motion.span>
          )}

          {discount >= 20 && (
            <span style={{
              position: 'absolute', top: 12, left: badgeStyle ? 'auto' : 12, right: badgeStyle ? 56 : 'auto',
              fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
              padding: '5px 10px', borderRadius: 999,
              background: 'rgba(79,122,83,0.95)', color: '#FFF',
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            }}>
              {discount}% OFF
            </span>
          )}

          {/* Wishlist button */}
          <motion.button
            onClick={handleWishlist}
            whileTap={{ scale: 0.85 }}
            whileHover={{ scale: 1.1 }}
            style={{
              position: 'absolute', top: 10, right: 10,
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: inWish ? 'var(--primary)' : 'var(--ink-2)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            <FiHeart size={15} fill={inWish ? 'currentColor' : 'none'} />
          </motion.button>

          {/* Color dots */}
          {product.colors?.length > 1 && (
            <motion.div
              variants={{ rest: { y: 8, opacity: 0.8 }, hover: { y: 0, opacity: 1 } }}
              transition={{ duration: 0.25 }}
              style={{ position: 'absolute', bottom: 10, left: 12, display: 'flex', gap: 5 }}
            >
              {product.colors.slice(0, 4).map(c => (
                <span key={c.hex} style={{
                  width: 11, height: 11, borderRadius: '50%', background: c.hex,
                  border: '1.5px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                }} />
              ))}
              {product.colors.length > 4 && (
                <span style={{ fontSize: 9, fontWeight: 700, color: '#FFF', alignSelf: 'center', marginLeft: 2, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
                  +{product.colors.length - 4}
                </span>
              )}
            </motion.div>
          )}
        </div>

        {/* Info */}
        <div style={{ padding: '14px 14px 16px' }}>
          <div style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 6 }}>
            {product.category?.name || 'VritiEthnic'}
          </div>
          <div className="serif" style={{
            fontSize: 16, lineHeight: 1.3, color: 'var(--ink)', marginBottom: 10,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            minHeight: 42,
          }}>
            {product.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span className="serif" style={{ fontSize: 17, fontWeight: 600, color: 'var(--ink)' }}>
              ₹{product.price?.toLocaleString('en-IN')}
            </span>
            {product.mrp > product.price && (
              <span style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'line-through' }}>
                ₹{product.mrp?.toLocaleString('en-IN')}
              </span>
            )}
          </div>
          {product.rating > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 8px', borderRadius: 999,
                background: 'var(--gold-soft)', color: 'var(--gold)',
                fontSize: 11, fontWeight: 700,
              }}>
                ★ {product.rating}
              </span>
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>({product.reviewCount})</span>
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
}

export function KurtiPlaceholder({ color = '#7A1F2B', size = '100%' }) {
  return (
    <svg viewBox="0 0 200 280" width={size} height={size} style={{ display: 'block' }}>
      <rect width="200" height="280" fill={color} opacity="0.12" />
      <g opacity="0.85">
        <path d="M65 40 L80 20 L120 20 L135 40 L160 50 L155 90 L140 90 L140 260 L60 260 L60 90 L45 90 L40 50 Z" fill={color} opacity="0.7" />
        <path d="M85 20 Q100 35 115 20" fill="none" stroke={color} strokeWidth="2" opacity="0.9" />
        <path d="M65 40 L45 90 M135 40 L155 90" stroke={color} strokeWidth="3" opacity="0.5" />
        <rect x="80" y="120" width="12" height="12" fill={color} opacity="0.3" rx="1" />
        <rect x="108" y="120" width="12" height="12" fill={color} opacity="0.3" rx="1" />
        <rect x="80" y="148" width="12" height="12" fill={color} opacity="0.3" rx="1" />
        <rect x="108" y="148" width="12" height="12" fill={color} opacity="0.3" rx="1" />
        <rect x="94" y="134" width="12" height="12" fill={color} opacity="0.4" rx="1" />
        <line x1="65" y1="260" x2="135" y2="260" stroke="#B08A3E" strokeWidth="3" opacity="0.6" />
      </g>
    </svg>
  );
}
